import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@components/buttons/Button';
import LoadingSpinner from '@components/Spinner/LoadingSpinner';
import { AvailableDrawTypes } from '@utils/DrawControlsClass';
import { LambdaOperation, LambdaOperationField, LambdaOperationMetadata } from '@api/lambdaOperationsApi';
import {
    getLambdaFieldDefaultValue,
    getLambdaFieldLabel,
    getLambdaOperationTitle,
    normalizeFieldType,
} from '../lambdaOperations.utils';
import { ApiError } from '@api/RestClient';
import LambdaGeometryField from './LambdaGeometryField';

type Props = {
    operation: LambdaOperation | null;
    metadata: LambdaOperationMetadata | null;
    loading: boolean;
    starting: boolean;
    error: ApiError | null;
    presetId?: string;
    onSubmit: (payload: Record<string, unknown>) => Promise<void>;
};

const getInitialValues = (fields: LambdaOperationField[]): Record<string, unknown> =>
    fields.reduce((acc, field) => {
        acc[field.name] = getLambdaFieldDefaultValue(field);
        return acc;
    }, {} as Record<string, unknown>);

const isNil = (value: unknown): value is null | undefined =>
    value === null || value === undefined;

const isEmptyFieldValue = (value: unknown): boolean =>
    value === '' || isNil(value) || (Array.isArray(value) && !value.length);

const parseFieldValue = (field: LambdaOperationField, value: unknown): unknown => {
    const type = normalizeFieldType(field.type);

    if (type === 'number' && !field.validationRegex) {
        return value === '' || isNil(value) ? null : Number(value);
    }
    if (type === 'boolean') {
        return Boolean(value);
    }
    return value;
};

const isPresetIdField = (field: LambdaOperationField): boolean =>
    ['presetid', 'preset.id', 'preset'].includes(String(field.name || field.key || '').toLowerCase());

const omitPresetFields = (
    values: Record<string, unknown>,
    fields: LambdaOperationField[]
): Record<string, unknown> => {
    const presetFieldNames = new Set(fields.filter(isPresetIdField).map(field => field.name));

    return Object.entries(values).reduce((acc, [key, value]) => {
        if (!presetFieldNames.has(key)) {
            acc[key] = value;
        }
        return acc;
    }, {} as Record<string, unknown>);
};

const normalizeSubmitValue = (field: LambdaOperationField, value: unknown): unknown => {
    const type = normalizeFieldType(field.type);

    if (type === 'number') {
        return value === '' || isNil(value) ? null : Number(value);
    }
    if (type === 'time' && typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
        return `${value}:00`;
    }

    return value;
};

const buildSubmitPayload = (
    values: Record<string, unknown>,
    fields: LambdaOperationField[]
): Record<string, unknown> => {
    const fieldsByName = new Map(fields.map(field => [field.name, field]));

    return Object.entries(omitPresetFields(values, fields)).reduce((acc, [key, value]) => {
        const field = fieldsByName.get(key);
        if (
            field &&
            !field.required &&
            isEmptyFieldValue(value)
        ) {
            return acc;
        }

        acc[key] = field ? normalizeSubmitValue(field, value) : value;
        return acc;
    }, {} as Record<string, unknown>);
};

const getGeometryDrawMode = (
    field: LambdaOperationField
): Exclude<AvailableDrawTypes, 'AllControls'> => {
    const type = String(field.type || '').toLowerCase();

    if (type === 'point') {
        return 'Point';
    }
    if (type === 'linestring' || type === 'line-string') {
        return 'LineString';
    }

    return 'Polygon';
};

const isFieldPatternInvalid = (field: LambdaOperationField, value: unknown): boolean => {
    if (!field.validationRegex || value === '' || isNil(value)) {
        return false;
    }

    try {
        return !new RegExp(field.validationRegex).test(String(value));
    } catch {
        return false;
    }
};

const renderFieldLabel = (label: string, required?: boolean) => (
    <span className="lambda-run-form__label">
        {label}
        {required ? <span className="lambda-run-form__asterisk">*</span> : null}
    </span>
);

const LambdaRunForm = ({
    operation,
    metadata,
    loading,
    starting,
    error,
    presetId,
    onSubmit,
}: Props) => {
    const [values, setValues] = useState<Record<string, unknown>>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [activeGeometryFieldName, setActiveGeometryFieldName] = useState<string | null>(null);
    const { t } = useTranslation();

    const fields = useMemo(() => metadata?.fields || [], [metadata]);

    useEffect(() => {
        setSubmitAttempted(false);
        setActiveGeometryFieldName(null);
        const presetValues = fields.reduce((acc, field) => {
            if (presetId && isPresetIdField(field)) {
                acc[field.name] = presetId;
            }
            return acc;
        }, {} as Record<string, unknown>);

        setValues({
            ...getInitialValues(fields),
            ...presetValues,
        });
    }, [fields, presetId]);

    const invalidRequiredFields = useMemo(
        () =>
            fields.filter(field => {
                if (isPresetIdField(field)) {
                    return false;
                }

                const value = values[field.name];
                return field.required && isEmptyFieldValue(value);
            }),
        [fields, values]
    );
    const invalidPatternFields = useMemo(
        () =>
            fields.filter(field => !isPresetIdField(field) && isFieldPatternInvalid(field, values[field.name])),
        [fields, values]
    );
    const isSubmitDisabled =
        loading ||
        starting ||
        !!error ||
        !fields.length ||
        !!invalidRequiredFields.length ||
        !!invalidPatternFields.length;

    const updateValue = (field: LambdaOperationField, value: unknown) => {
        const parsedValue = parseFieldValue(field, value);
        setValues(prev => {
            if (prev[field.name] === parsedValue) {
                return prev;
            }

            return { ...prev, [field.name]: parsedValue };
        });
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitAttempted(true);

        if (invalidRequiredFields.length || invalidPatternFields.length) {
            return;
        }

        await onSubmit(buildSubmitPayload(values, fields));
    };

    const renderField = (field: LambdaOperationField) => {
        const type = normalizeFieldType(field.type);
        const label = getLambdaFieldLabel(field);
        const value = values[field.name];
        const options = Array.isArray(field.options) ? field.options : [];
        const disabled = starting || (isPresetIdField(field) && !!presetId);
        const hasError =
            submitAttempted &&
            field.required &&
            isEmptyFieldValue(value);
        const hasPatternError =
            submitAttempted && isFieldPatternInvalid(field, value);

        if (isPresetIdField(field)) {
            return null;
        }

        if (type === 'boolean') {
            return (
                <label className="lambda-run-form__checkbox" key={field.name}>
                    <input
                        name={field.name}
                        type="checkbox"
                        checked={Boolean(value)}
                        disabled={disabled}
                        onChange={event => updateValue(field, event.target.checked)}
                    />
                    {renderFieldLabel(label, field.required)}
                </label>
            );
        }

        if (type === 'select') {
            return (
                <label className="lambda-run-form__field" key={field.name}>
                    {renderFieldLabel(label, field.required)}
                    <select
                        name={field.name}
                        required={field.required}
                        disabled={disabled}
                        value={String(value ?? '')}
                        onChange={event => updateValue(field, event.target.value)}
                    >
                        <option value="" disabled>
                            {field.placeholder || t('lambda-operations.select-placeholder')}
                        </option>
                        {isPresetIdField(field) && presetId && !options.some(option => String(option.value) === presetId) ? (
                            <option value={presetId}>{presetId}</option>
                        ) : null}
                        {options.map(option => (
                            <option key={String(option.value)} value={String(option.value)}>
                                {option.caption || String(option.value)}
                            </option>
                        ))}
                    </select>
                    {hasError ? <small>{t('lambda-operations.required-field')}</small> : null}
                    {hasPatternError ? <small>{field.validationRegex}</small> : null}
                </label>
            );
        }

        if (type === 'multi-select') {
            return (
                <label className="lambda-run-form__field" key={field.name}>
                    {renderFieldLabel(label, field.required)}
                    <select
                        name={field.name}
                        multiple
                        disabled={disabled}
                        value={Array.isArray(value) ? value.map(String) : []}
                        onChange={event =>
                            updateValue(
                                field,
                                Array.from(event.target.selectedOptions).map(option => option.value)
                            )
                        }
                    >
                        {options.map(option => (
                            <option key={String(option.value)} value={String(option.value)}>
                                {option.caption || String(option.value)}
                            </option>
                        ))}
                    </select>
                    {hasError ? <small>{t('lambda-operations.required-field')}</small> : null}
                </label>
            );
        }

        if (type === 'geometry') {
            return (
                <div className="lambda-run-form__geometry" key={field.name}>
                    <LambdaGeometryField
                        name={field.name}
                        label={label}
                        initialValue={String(getLambdaFieldDefaultValue(field) ?? '')}
                        value={String(value ?? '')}
                        drawMode={getGeometryDrawMode(field)}
                        required={field.required}
                        disabled={disabled}
                        active={activeGeometryFieldName === field.name}
                        onActivate={() => setActiveGeometryFieldName(field.name)}
                        onDeactivate={() => setActiveGeometryFieldName(null)}
                        onChange={fieldValue => updateValue(field, fieldValue)}
                    />
                    {field.helpText || field.description ? (
                        <em>{field.helpText || field.description}</em>
                    ) : null}
                    {hasError ? <small>{t('lambda-operations.required-field')}</small> : null}
                </div>
            );
        }

        const inputType =
            (type === 'number' && !field.validationRegex) || type === 'time' || type === 'date' || type === 'datetime-local'
                ? type
                : 'text';

        return (
            <label className="lambda-run-form__field" key={field.name}>
                {renderFieldLabel(label, field.required)}
                <input
                    name={field.name}
                    type={inputType}
                    required={field.required}
                    disabled={disabled}
                    value={String(value ?? '')}
                    min={field.min}
                    max={field.max}
                    step={type === 'time' ? field.step ?? 1 : field.step}
                    placeholder={field.placeholder}
                    pattern={field.validationRegex}
                    onChange={event => updateValue(field, event.target.value)}
                />
                {field.helpText || field.description ? (
                    <em>{field.helpText || field.description}</em>
                ) : null}
                {hasError ? <small>{t('lambda-operations.required-field')}</small> : null}
                {hasPatternError ? <small>{field.validationRegex}</small> : null}
            </label>
        );
    };

    if (!operation) {
        return null;
    }

    return (
        <form className="lambda-run-form" onSubmit={handleSubmit} noValidate data-testid="lambdaRunForm">
            <div className="lambda-section-heading">
                <p>{t('lambda-operations.parameters')}</p>
                <span>{getLambdaOperationTitle(operation)}</span>
            </div>
            {loading ? (
                <div className="lambda-operations-empty">
                    <LoadingSpinner size="18px" backgroundColor="transparent" />
                    <span>{t('lambda-operations.loading-metadata')}</span>
                </div>
            ) : error ? (
                <div className="lambda-operations-empty">
                    {t('lambda-operations.failed-load-metadata')}
                </div>
            ) : fields.length ? (
                fields.map(renderField)
            ) : (
                <div className="lambda-operations-empty">
                    {t('lambda-operations.no-metadata')}
                </div>
            )}
            <Button
                type="submit"
                text={starting ? t('lambda-operations.starting') : t('lambda-operations.start')}
                colorType="dark"
                disabled={isSubmitDisabled}
                testId="lambdaStartBtn"
            />
            {submitAttempted && (invalidRequiredFields.length || invalidPatternFields.length) ? (
                <p className="lambda-run-form__error">
                    {t('common.not-all-required-fields-are-filled')}
                </p>
            ) : null}
        </form>
    );
};

export default LambdaRunForm;
